import type { TFunction } from "i18next";
import type { Trip } from "../types";
import {
  getStatusBadgeClass,
  getStatusBadgeStyle,
  getStatusBadgeLabel,
} from "../../../../../../utils/getStatusEntity";

// Tailwind classes for the trip status badge. Returns "" when the badge
// should be styled via inline style (custom backend color). Pair with
// `getStatusStyle` in the consumer.
export function getStatusColor(trip: Trip): string {
  return getStatusBadgeClass(trip.status);
}

// Inline style for custom-colored statuses. Undefined when a class covers it.
export function getStatusStyle(
  trip: Trip,
): { backgroundColor: string; color: string } | undefined {
  return getStatusBadgeStyle(trip.status);
}

// Localized label for the trip status, with a sensible fallback.
export function getStatusLabel(trip: Trip, t: TFunction): string {
  return getStatusBadgeLabel(
    trip.status,
    t,
    t("trips.status.noStatus", { defaultValue: "N/A" }),
  );
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case "urgent":
      return "bg-red-100 text-red-800";
    case "high":
      return "bg-orange-100 text-orange-800";
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "low":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

export function getPriorityLabel(priority: string, t: TFunction): string {
  switch (priority) {
    case "urgent":
      return t("trips.priority.urgent");
    case "high":
      return t("trips.priority.high");
    case "medium":
      return t("trips.priority.medium");
    case "low":
      return t("trips.priority.low");
    default:
      return priority || "N/A";
  }
}

export function formatTripDate(date: { _seconds: number; _nanoseconds: number } | string | undefined): string {
  if (!date) return "N/A";
  if (typeof date === "string") {
    const d = new Date(date);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  if (date._seconds) {
    const d = new Date(date._seconds * 1000);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }
  return "N/A";
}
